import { Sidebar as BaseSidebar, MobileNavigation, Select, SelectOptionType } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { AppLayoutContext } from '../../contexts/AppLayoutContextProvider';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useNavigationItems } from '../../hooks/useNavigationItems';
import { useSchoolYearSelect } from '../../hooks/useSchoolYearSelect';
import { HelpMenu } from '../HelpMenu/HelpMenu';
import styles from './Sidebar.module.scss';

export const Sidebar = observer(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { pimAuthClaims } = useAuthClaims();
  const { sidebarIsFixed, setSidebarIsFixed, sidebarIsOpen, setSidebarIsOpen, mobileMenuIsOpen, setMobileMenuIsOpen } =
    useContext(AppLayoutContext);

  const { mainItems, footerItems } = useNavigationItems();
  const schoolYear = useSchoolYearSelect();

  const [sidebarToggleState, setSidebarToggleState] = useState(false);

  useEffect(() => {
    if (sidebarIsFixed !== sidebarToggleState) {
      setSidebarToggleState(sidebarIsFixed);
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    }
  }, [sidebarIsFixed, sidebarToggleState]);

  // Menü schließen, wenn das Layout auf Desktop wechselt (z. B. Tablet drehen)
  useEffect(() => {
    if (!isMobile) setMobileMenuIsOpen(false);
  }, [isMobile, setMobileMenuIsOpen]);

  if (isMobile) {
    return (
      <MobileNavigation
        currentPath={location.pathname}
        footerItems={footerItems}
        isOpen={mobileMenuIsOpen}
        logoType='planung'
        mainItems={mainItems}
        subtitle={pimAuthClaims.getProfile()?.organization?.name}
        menuLabel={t('navigation.menu')}
        closeLabel={t('navigation.closeMenu')}
        footer={
          <>
            <Select
              name='year'
              label={t('common.schoolYear')}
              isMulti={false}
              isSearchable={false}
              disabled={schoolYear.isDisabled}
              options={schoolYear.options}
              value={schoolYear.selectedOption}
              onChange={(option) => {
                const opt = option as SingleValue<SelectOptionType>;
                schoolYear.selectSchoolYear(opt?.value as string);
              }}
            />
            <HelpMenu label={t('about.helpAndContact')} className={styles['help-menu']} />
          </>
        }
        onClose={() => setMobileMenuIsOpen(false)}
        onNavigate={(path) => navigate(path)}
      />
    );
  }

  return (
    <BaseSidebar
      isDesktop={true}
      sidebarContent={mainItems}
      sidebarFooterItems={footerItems}
      isExpanded={sidebarIsOpen}
      setIsExpanded={setSidebarIsOpen}
      isFixed={sidebarIsFixed}
      setIsFixed={setSidebarIsFixed}
      logoType={'planung'}
      currentLocationPath={location.pathname}
      className={classNames(styles.sidebar, 'has-scrollbar')}
    />
  );
});

Sidebar.displayName = 'Sidebar';
